// src/components/AuditThresholdsPopup.jsx
// Sets the account's standing Payment Audit Thresholds — a shared policy
// ("flag any tracked provider whose combined current-year payments cross
// this line for audit"), independent of the per-finding review thresholds
// used elsewhere. Kept visually and conceptually distinct from those on
// purpose: this decides what becomes a finding at all; the other decides
// when to re-review an existing one.

import { useState, useEffect } from "react";

export default function AuditThresholdsPopup({ userId, fetchFn, onClose }) {
  const [general, setGeneral] = useState("");
  const [ownership, setOwnership] = useState("");
  const [directResearch, setDirectResearch] = useState("");
  const [piResearch, setPiResearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFn("/api/manage-audit-thresholds", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, action: "get" }),
    })
      .then((res) => res.json())
      .then((data) => {
        const t = data.thresholds;
        if (t) {
          setGeneral(t.general_payments_threshold ?? "");
          setOwnership(t.ownership_threshold ?? "");
          setDirectResearch(t.direct_research_threshold ?? "");
          setPiResearch(t.pi_research_threshold ?? "");
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId, fetchFn]);

  async function handleSave() {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetchFn("/api/manage-audit-thresholds", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          action: "set",
          generalPaymentsThreshold: general === "" ? null : parseFloat(general),
          ownershipThreshold: ownership === "" ? null : parseFloat(ownership),
          directResearchThreshold: directResearch === "" ? null : parseFloat(directResearch),
          piResearchThreshold: piResearch === "" ? null : parseFloat(piResearch),
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error || "Failed to save. Please try again.");
        return;
      }
      onClose();
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-navy-800 border border-white/10 rounded-xl p-5 max-w-md w-full space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className="text-white font-medium text-sm">Payment Audit Thresholds</h3>
          <p className="text-white/40 text-xs mt-1">
            Automatically flag any tracked provider whose combined current-program-year payments
            reach or exceed these amounts — independent of any specific company relationship.
            Shared across your whole account; any team member can adjust these, and it applies to
            everyone's future scans. Leave a field blank to disable that category.
          </p>
        </div>

        {loading ? (
          <p className="text-white/30 text-xs">Loading current settings…</p>
        ) : (
          <div className="space-y-2">
            <label className="flex items-center justify-between gap-2 text-xs text-white/60">
              <span>General payments</span>
              <input
                type="number"
                placeholder="disabled"
                value={general}
                onChange={(e) => setGeneral(e.target.value)}
                className="w-32 bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs text-right focus:outline-none focus:border-gold-500/40"
              />
            </label>
            <label className="flex items-center justify-between gap-2 text-xs text-white/60">
              <span>Ownership value</span>
              <input
                type="number"
                placeholder="disabled"
                value={ownership}
                onChange={(e) => setOwnership(e.target.value)}
                className="w-32 bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs text-right focus:outline-none focus:border-gold-500/40"
              />
            </label>
            <label className="flex items-center justify-between gap-2 text-xs text-white/60">
              <span>Direct research payments</span>
              <input
                type="number"
                placeholder="disabled"
                value={directResearch}
                onChange={(e) => setDirectResearch(e.target.value)}
                className="w-32 bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs text-right focus:outline-none focus:border-gold-500/40"
              />
            </label>
            <label className="flex items-center justify-between gap-2 text-xs text-white/60">
              <span>PI research funding</span>
              <input
                type="number"
                placeholder="disabled"
                value={piResearch}
                onChange={(e) => setPiResearch(e.target.value)}
                className="w-32 bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs text-right focus:outline-none focus:border-gold-500/40"
              />
            </label>
            <p className="text-white/25 text-xs pt-1">
              PI research funding isn't a direct payment to the provider, but is still shown for context.
            </p>
          </div>
        )}

        {error && <p className="text-red-400 text-xs">{error}</p>}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button onClick={onClose} className="text-white/40 hover:text-white/70 text-xs px-3 py-1.5">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="text-xs px-4 py-1.5 rounded-lg bg-gold-500/10 text-gold-400 hover:bg-gold-500/20 disabled:opacity-50 transition-all font-medium"
          >
            {saving ? "Saving…" : "Save Thresholds"}
          </button>
        </div>
      </div>
    </div>
  );
}